import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import * as moment from 'moment';
import { AIService } from 'src/app/ai/ai.service';
import { TimesheetsService } from './timesheets.service';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';

@Injectable({ providedIn: 'root' })
export class TimesheetAiValidatorService {
  constructor(private ai: AIService, private timesheetsService: TimesheetsService, private http: HttpClient) {}

  async validate(
    pdfUrl: string,
    ctx: {
      employeeName: string;
      expectedHours: number; // from selected timeslot(s)
      monthLabel?: string; // e.g. "January 2026"
      uploadedMonthISO?: string; // e.g. "2026-01-01"
      timesheetTypeHint?: string; // project.timesheetType
    }
  ): Promise<{ ok: boolean; issues: string[]; type?: string; details?: Record<string, any> }> {
    // Try to detect the month from the PDF URL or filename (e.g. "2025-05")
    const detectedIso = this.detectMonthIsoFromUrl(pdfUrl);
    const uploadedISO = detectedIso || ctx.uploadedMonthISO || null;
    const uploadedMonthText = uploadedISO ? moment(uploadedISO).format('MMMM YYYY') : '';

    // Delegate to AI (no local PDF parsing)
    try {
      console.info('[AI VALIDATOR] Uploading and validating timesheet PDF via OpenAI Responses …');
        const result = await this.aiValidatePdf(pdfUrl, {
        employeeName: ctx.employeeName,
        expectedHours: ctx.expectedHours,
        uploadedMonthText,
        timesheetTypeHint: ctx.timesheetTypeHint || ''
      });
      console.info('[AI VALIDATOR] Validation result:', result);
      return {
        ok: (result.issues || []).length === 0,
        issues: result.issues || [],
        type: result.type || undefined,
          details: {
            parsedHours: result.parsedHours ?? null,
            pdfMonthISO: result.pdfMonthISO ?? null,
            expectedMonthISO: uploadedISO,
            hoursOk: result.hoursOk ?? undefined,
            monthOk: result.monthOk ?? undefined,
            signed: result.signed ?? undefined,
            employeePresent: result.employeePresent ?? undefined
          }
      };
    } catch (e) {
      const msg = (e?.message || '').toString().trim();
      console.warn('[AI VALIDATOR] Failed:', msg || e);
      return { ok: false, issues: ['AI-validatie kon niet worden uitgevoerd.' + (msg ? ` (${msg})` : '')], type: undefined };
    }
  }

  private async aiValidatePdf(
    pdfUrl: string,
    args: { employeeName?: string; expectedHours?: number; uploadedMonthText?: string; timesheetTypeHint?: string }
    ): Promise<{ issues: string[]; type?: string; parsedHours?: number | null; pdfMonthISO?: string | null; hoursOk?: boolean; monthOk?: boolean; signed?: boolean; employeePresent?: boolean }> {
    // Prefer authenticated retrieval via Angular service for backend URLs
    let fileId: string;
    const isBackendRetrieve = (
      pdfUrl.includes('/api/timesheets/retrieveFile/') ||
      pdfUrl.includes('/timesheets/retrieveFile/')
    );
    console.groupCollapsed('%c[AI VALIDATOR] aiValidatePdf start', 'color:#00bfa5;font-weight:bold;');
    console.info('pdfUrl:', pdfUrl);
    console.info('args:', {
      employeeName: args.employeeName,
      expectedHours: args.expectedHours,
      uploadedMonthText: args.uploadedMonthText,
      timesheetTypeHint: args.timesheetTypeHint
    });
    console.info('isBackendRetrieve:', isBackendRetrieve);
    if (isBackendRetrieve) {
      try {
        const uuid = (pdfUrl.split('/').pop() || '').split('?')[0];
        if (!uuid) throw new Error('Kon timesheet UUID niet afleiden uit URL');
        console.info('[AI VALIDATOR] Using backend upload-from-timeslot with uuid:', uuid);
        // New: ask backend to upload directly from timeslot UUID (no retrieveFile call)
        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        const client = (environment as any).OPENAI_CLIENT_AUTH;
        if (client && String(client).trim().length) headers['X-Client-Auth'] = String(client).trim();
        const resp = await firstValueFrom(
          this.http.post<any>(
            `${environment.baseUrl}/openai/files/upload-from-timeslot/${uuid}`,
            {},
            { headers: new HttpHeaders(headers), observe: 'response' }
          )
        );
        const body = resp?.body;
        console.debug('[AI VALIDATOR] upload-from-timeslot response (raw):', body);
        // Handle both already-parsed JSON objects and raw strings defensively
        if (typeof body === 'string') {
          try {
            const j = JSON.parse(body as any);
            fileId = j?.id || j?.file?.id || '';
          } catch {
            fileId = '';
          }
        } else {
          fileId = body?.id || body?.file?.id || '';
        }
        console.info('[AI VALIDATOR] Parsed fileId from backend:', fileId);
        if (!fileId) throw new Error('Upload via backend (timeslot) faalde');
      } catch (e: any) {
        console.warn('[AI VALIDATOR] Backend upload-from-timeslot failed, falling back to direct URL upload:', e?.message || e);
        fileId = await this.ai.uploadFileFromUrl(pdfUrl);
        console.info('[AI VALIDATOR] Fallback fileId:', fileId);
      }
    } else {
      // Upload PDF from external URL
      console.debug('[AI VALIDATOR] Uploading file to OpenAI Files:', pdfUrl);
      fileId = await this.ai.uploadFileFromUrl(pdfUrl);
      console.info('[AI VALIDATOR] External upload fileId:', fileId);
    }
    console.debug('[AI VALIDATOR] File uploaded, id:', fileId);
    console.groupEnd();
    return this.ai.validateTimesheetPdfWithResponses({
      fileId,
      employeeName: args.employeeName,
      expectedHours: args.expectedHours,
      uploadedMonthText: args.uploadedMonthText,
      timesheetTypeHint: args.timesheetTypeHint
    });
  }

  private detectMonthIsoFromUrl(url: string): string | null {
    try {
      // common patterns: 2025-05, 2025_05, 202505
      const m1 = url.match(/(20\d{2})[-_\/](0[1-9]|1[0-2])/);
      if (m1) {
        const y = m1[1];
        const m = m1[2];
        return `${y}-${m}-01`;
      }
      const m2 = url.match(/(20\d{2})(0[1-9]|1[0-2])/);
      if (m2) {
        const y = m2[1];
        const m = m2[2];
        return `${y}-${m}-01`;
      }
    } catch {}
    return null;
  }
}
